import React, { useCallback, useState } from 'react';

import { Combobox, Input, InputBase, ScrollArea, useCombobox } from '@mantine/core';

import { Component } from 'models';

import { ComponentOverviewHitSimple } from 'components/component-overview/ComponentOverviewHit.Simple';

type ProductsSelectProps = {
    onOptionSubmit: (value: string) => void;
    components?: Component[];
    manufacturer?: string;
    isLoading?: boolean;
};

const ProductsSelect = ({ onOptionSubmit, components = [], manufacturer, isLoading }: ProductsSelectProps) => {
    const combobox = useCombobox();

    const [query, setQuery] = useState('');

    const data = components
        .map((component) => ({
            value: component.id,
            label: [component.name, component.productIdentifier, component.productSeries, component.type]
                .filter(Boolean)
                .join(' '),
        }))
        .filter((component) => {
            return component.label.toLowerCase().includes(query.toLowerCase());
        });

    const onOptionSubmitWrapper = async (value: string) => {
        combobox.closeDropdown();
        onOptionSubmit(value);
        setQuery('');
    };

    const getRenderOption = useCallback(
        (componentId: string) => {
            const component = components.find(({ id }) => componentId === id);

            if (!component) return null;

            return <ComponentOverviewHitSimple component={component} onClick={(event) => event.preventDefault()} />;
        },
        [components],
    );

    return (
        <Input.Wrapper style={{ flexGrow: 1 }}>
            <Combobox store={combobox} onOptionSubmit={onOptionSubmitWrapper}>
                <Combobox.Target>
                    <InputBase
                        value={query}
                        rightSection={<Combobox.Chevron />}
                        rightSectionPointerEvents="none"
                        onChange={(event) => {
                            setQuery(event.currentTarget.value);
                        }}
                        onClick={() => {
                            combobox.openDropdown();
                        }}
                        onFocus={() => {
                            combobox.openDropdown();
                        }}
                        onBlur={() => {
                            combobox.closeDropdown();
                        }}
                        placeholder={
                            !manufacturer
                                ? 'Choose a manufacturer first'
                                : components.length
                                  ? 'Choose product'
                                  : isLoading
                                    ? 'Loading products...'
                                    : 'Manufacturer has no products'
                        }
                        disabled={components.length === 0}
                    />
                </Combobox.Target>
                <Combobox.Dropdown>
                    <Combobox.Options>
                        <ScrollArea.Autosize type="always" mah={200}>
                            {data.map((option) => (
                                <Combobox.Option value={option.value} key={option.value}>
                                    {getRenderOption(option.value)}
                                </Combobox.Option>
                            ))}
                            {data.length === 0 && <Combobox.Empty>Nothing found</Combobox.Empty>}
                        </ScrollArea.Autosize>
                    </Combobox.Options>
                </Combobox.Dropdown>
            </Combobox>
        </Input.Wrapper>
    );
};

export { ProductsSelect };
